import { Helmet } from "react-helmet-async";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
// @mui
import {
  Card,
  Stack,
  Container,
  Typography,
  CircularProgress,
  Box,
  Tabs,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Button,
} from "@mui/material";
import { Edit } from "@mui/icons-material";
import Scrollbar from "../components/scrollbar";
import { get } from "src/utils/networkUtils";
import AttrLeaderboardTable from "../components/user-level-table/AttrLeaderboardTable";
import LevelOverallLeaderboardTable from "src/components/user-level-table/LevelOverallLeaderboardTable";

// ----------------------------------------------------------------------

export default function UserLevelsPage() {
  const [loading, setLoading] = useState(false);
  const [attributes, setAttributes] = useState(null);
  const [userLevels, setUserLevels] = useState(null);
  const [activeTab, setActiveTab] = useState(0);
  const navigate = useNavigate();

  useEffect(() => {
    get(`/admin/user-level/attributes`).then(function (response) {
      setLoading(false);
      setAttributes(response.attributes);
    });
    get(`/admin/user-level`).then(function (response) {
      setLoading(false);
      setUserLevels(response.levels);
    });
  }, []);

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  return (
    <>
      <Helmet>
        <title>User Levels</title>
      </Helmet>

      <Container>
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          mb={5}
        >
          <Typography variant="h4" gutterBottom>
            User Levels
          </Typography>
          <Button
            variant="contained"
            color="primary"
            startIcon={<Edit />}
            onClick={() => navigate('/dashboard/user-levels/edit')}
          >
            Player Value Attributes
          </Button>
        </Stack>
        <Card sx={{ mb: 3 }}>
          <Box sx={{ p: 3 }}>
            {userLevels && userLevels.length > 0 ? (
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Name</TableCell>
                      <TableCell>Overall Range (Top %)</TableCell>
                      <TableCell>Users</TableCell>
                      <TableCell>Color</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {userLevels.map((level) => (
                      <TableRow key={level.userValueLevelId || level.name}>
                        <TableCell>
                          <Typography variant="body2" fontWeight="medium">
                            {level.name}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {Math.round(level.startRange * 100)} - {Math.round(level.endRange * 100)}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" fontWeight="medium">
                            {level.userCount}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Box
                              sx={{
                                width: 20,
                                height: 20,
                                backgroundColor: level.color,
                                borderRadius: 1,
                                border: '1px solid',
                                borderColor: 'divider'
                              }}
                            />
                            <Chip
                              label={level.color}
                              size="small"
                              variant="outlined"
                              sx={{
                                fontFamily: 'monospace',
                                fontSize: '0.75rem'
                              }}
                            />
                          </Box>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            ) : (
              <Box sx={{ textAlign: 'center', py: 2 }}>
                <Typography variant="body2" color="text.secondary">
                  No user levels configured
                </Typography>
              </Box>
            )}
          </Box>
        </Card>
        {attributes && attributes.length > 0 && (
          <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
            <Tabs value={activeTab} onChange={handleTabChange}>
              <Tab key={0} label="Overall" />
              {attributes.map((attribute, index) => (
                <Tab key={attribute.userValueAttributeId} label={attribute.name} />
              ))}
            </Tabs>
          </Box>
        )}

        <Card>
          {attributes === null || loading ? (
            <Box
              sx={{ display: "flex", justifyContent: "center" }}
              style={{ padding: 20 }}
            >
              <CircularProgress />
            </Box>
          ) : attributes.length > 0 ? (
            <Scrollbar>
              <Box sx={{ p: 3 }}>
                {activeTab === 0 ? (
                  <>
                    <Typography variant="h6" gutterBottom>
                      Overall Level Leaderboard
                    </Typography>
                    <Typography variant="body2" color="text.secondary" mb={2}>
                      Combined scores from all attributes
                    </Typography>
                    <LevelOverallLeaderboardTable />
                  </>
                ) : (
                  <>
                    <Typography variant="h6" gutterBottom>
                      {attributes[activeTab - 1]?.name} Leaderboard
                    </Typography>
                    <Typography variant="body2" color="text.secondary" mb={2}>
                      Weight: {(attributes[activeTab - 1]?.weight * 100).toFixed(1)}%
                    </Typography>
                    <AttrLeaderboardTable attrId={attributes[activeTab - 1]?.userValueAttributeId} />
                  </>
                )}
              </Box>
            </Scrollbar>
          ) : (
            <Box sx={{ p: 3, textAlign: 'center' }}>
              <Typography variant="body1" color="text.secondary">
                No attributes found
              </Typography>
            </Box>
          )}
        </Card>
      </Container>
    </>
  );
}
